// ============================================================================
// BatchesContext — Phase 2.0.2
// ============================================================================
// Thin Provider that owns the broiler batch + broiler-edit-form useState hooks
// previously declared at the top of App(). Helpers and derived values remain
// in App.jsx; this module just holds the state.
//
//   batches         : broiler batches action-time mirror (seeded by route-local
//                     bridges where an App callback still needs it)
//   showForm        : boolean — broiler add/edit modal open
//   editId          : string|null — id of batch being edited
//   form            : object — current batch form state (defaults to EMPTY_FORM)
//   originalForm    : snapshot of form at edit-open time (for dirty detection)
//   conflicts       : array — scheduling conflicts detected by detectConflicts()
//   tooltip         : { id, x, y } | null — timeline hover tooltip
//   override        : boolean — manual override of schedule conflict warning
//   showLegacy      : boolean — legacy hatchery/breed dropdowns toggle
//   parsedProcessor : { avgDressed, avgBreast, avgThigh, birdCount, fileName } | null
//   docUploading    : boolean — batch-document upload in flight
//   deleteConfirm   : { message, onConfirm } | null — shared delete modal state
//
// EMPTY_FORM lives in src/app/data/broilerFormDefaults.ts — AppProviders passes
// it in as `formInit` so this file stays focused on state ownership.
// ============================================================================
import {createContext, useContext, useState} from 'react';
import type {ReactNode} from 'react';
import type {ScheduleConflict} from '../lib/conflicts.js';

// Value type INFERRED from the builder below, never hand-authored.
//
// One member gets a real domain type, and it is a BORROWED one rather than an
// invented one: `conflicts` is only ever assigned `detectConflicts(...)` or
// `[]` (App.jsx openAdd / openEdit / the openEdit catch — all three checked),
// and detectConflicts already declares its own return in the converted
// src/lib/conflicts.ts. The import is type-only, so it adds no runtime edge
// this module did not already have.
//
// The rest stay `unknown`, which is what this module can actually verify. A
// broiler batch, a processor-spreadsheet parse result and a timeline tooltip
// are all built elsewhere — the batch rows come from SQL, and this file never
// reads a field off any of them. The header above documents the shapes the
// WRITERS use; that documentation is not a checked contract, and turning it
// into one here would be inventing a row interface rather than describing the
// code. `form` follows `formInit`, which is deliberately opaque for the same
// reason (see WebformsConfigContext for the long form of that argument).
//
// SIGN-OUT NOTE, unchanged by this conversion: none of this state is reset when
// the session ends. That is existing, deliberate behaviour and is guarded
// elsewhere; nothing here should "fix" it.
function useBatchesValue(formInit: unknown) {
  const [batches, setBatches] = useState<unknown[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<unknown>(null);
  const [form, setForm] = useState(formInit);
  const [originalForm, setOriginalForm] = useState<unknown>(null);
  const [conflicts, setConflicts] = useState<ScheduleConflict[]>([]);
  const [tooltip, setTooltip] = useState<unknown>(null);
  const [override, setOverride] = useState(false);
  const [showLegacy, setShowLegacy] = useState(false);
  const [parsedProcessor, setParsedProcessor] = useState<unknown>(null);
  const [docUploading, setDocUploading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<unknown>(null);
  // Inline notice surfaced inside the broiler add/edit modal. Shared so
  // App's BatchForm helpers (parseProcessorXlsx + submit) can write
  // through the same banner the BatchForm UI renders.
  const [formNotice, setFormNotice] = useState<unknown>(null);

  return {
    batches,
    setBatches,
    showForm,
    setShowForm,
    editId,
    setEditId,
    form,
    setForm,
    originalForm,
    setOriginalForm,
    conflicts,
    setConflicts,
    tooltip,
    setTooltip,
    override,
    setOverride,
    showLegacy,
    setShowLegacy,
    parsedProcessor,
    setParsedProcessor,
    docUploading,
    setDocUploading,
    deleteConfirm,
    setDeleteConfirm,
    formNotice,
    setFormNotice,
  };
}

type BatchesContextValue = ReturnType<typeof useBatchesValue>;

const BatchesContext = createContext<BatchesContextValue | null>(null);

export function BatchesProvider({children, formInit}: {children?: ReactNode; formInit: unknown}) {
  const value = useBatchesValue(formInit);
  return <BatchesContext.Provider value={value}>{children}</BatchesContext.Provider>;
}

// Asserts the provider, and that assertion is what gives every consumer a
// non-null type. Not a new failure mode: the hook used to return `null` outside
// its provider and every consumer destructures the result immediately, so the
// app ALREADY crashed at that exact line with an anonymous
// `TypeError: Cannot destructure property … of null`. Same failure, same point,
// named cause.
export function useBatches(): BatchesContextValue {
  const value = useContext(BatchesContext);
  if (!value) throw new Error('useBatches must be used within BatchesProvider');
  return value;
}
